import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import {RatingUnitService} from '../../services/rating-unit.service';
import {SelectionProductFamily} from '../../model/selection-product-family';
import {SelectionProductLocation} from '../../model/selection-product-location';
import {SelectionSystemType} from '../../model/selection-system-type';
import {SelectionItem} from '../../selections/selection-item';
import {MotorType} from '../../model/motor-type';
import {FluidType} from '../../model/fluid-type';
import {FilterType} from '../../model/filter-type';
import {UnitSelectionCriteria} from '../../model/unit-selection-criteria';
import {UnitSelectionCandidate} from '../../model/unit-selection-candidate';
import {RatingsModel} from '../../model/ratings-model';
import {Router} from '@angular/router';
import {UnitSelectionService} from '../../services/unit-selection-service';
import {MatDialog} from '@angular/material/dialog';
import {CoilSelectionCriteria} from '../../model/coil-selection-criteria';
import { HttpErrorResponse } from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {RatingUnit} from '../../model/rating-unit';
import {PersonLogin} from '../../person/person-login';
import {PersonLoginService} from '../../services/person-login-service';
import {AddScheduleLineDialogComponent} from '../../dialogs/add-schedule-line-dialog/add-schedule-line-dialog.component';
import {ScheduleLineInput} from '../../model/schedule-line-input';
import {ScheduleService} from '../../services/schedule.service';
import {StringUtils} from '../../utils/string-utils';
import {ConditionValueLimit} from '../../model/condition-value-limit';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SelectionCandidateGridComponent } from '../../schedules/selection-candidate-grid/selection-candidate-grid.component';
import { HeatingPerformanceInputsComponent } from '../../schedules/heating-performance-inputs/heating-performance-inputs.component';
import { CoolingPerformanceInputsComponent } from '../../schedules/cooling-performance-inputs/cooling-performance-inputs.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatOption } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatFormField, MatLabel } from '@angular/material/form-field';


@Component({
    selector: 'app-selection-tool',
    templateUrl: './selection-tool.component.html',
    styleUrls: ['./selection-tool.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatLabel, MatSelect, FormsModule, MatOption, MatTooltip, MatButton, MatIcon, MatInput, MatCheckbox, CoolingPerformanceInputsComponent, HeatingPerformanceInputsComponent, SelectionCandidateGridComponent, MatProgressSpinner]
})
export class SelectionToolComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly selectionService = inject(UnitSelectionService);
  private readonly dialog = inject(MatDialog);
  private readonly ratingUnitService = inject(RatingUnitService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly scheduleService = inject(ScheduleService);


  readonly families = signal<SelectionProductFamily[]>(undefined!);
  readonly locations = signal<SelectionProductLocation[]>(undefined!);
  readonly systemTypes = signal<SelectionSystemType[]>(undefined!);
  readonly motorVoltages = signal<SelectionItem[]>(undefined!);
  readonly motorTypes = signal<MotorType[]>(undefined!);
  readonly fluidTypes = signal<FluidType[]>(undefined!);
  readonly filterTypes = signal<FilterType[]>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  unitSelectionCriteria!: UnitSelectionCriteria;
  readonly showCoolingInputs = signal<boolean>(false);
  readonly showHeatingInputs = signal<boolean>(false);
  readonly showHydronicHeating = signal<boolean>(false);
  readonly candidates = signal<UnitSelectionCandidate[]>(undefined!);
  readonly allModels = signal<RatingsModel[]>(undefined!);
  availableModels!: RatingsModel[];
  readonly resultMessage = signal<string>(undefined!);
  readonly previousState = signal<UnitSelectionCriteria>(undefined!);
  readonly isRefrigerant = signal<boolean>(undefined!);
  readonly isSteam = signal<boolean>(undefined!);
  personLogin!: PersonLogin;
  readonly scheduleId = input<string>(undefined!);
  readonly conditionLimits = signal<ConditionValueLimit[]>(undefined!);

  ngOnInit(): void {
    this.isBusy.set(true);
    this.personLogin = this.personLoginService.getCachedLogin()!;
    this.unitSelectionCriteria = new UnitSelectionCriteria();
    this.unitSelectionCriteria.locationId = 0;
    this.unitSelectionCriteria.familyId = 0;
    this.selectionService.getConditionValueLimits().subscribe({
      next: (limits) => {
        this.conditionLimits.set(limits);
        this.selectionService.getRatingsModels().subscribe({
          next: (models) => {
            this.allModels.set(models);
            this.selectionService.getFluidTypes().subscribe({
              next: (fluidTypes) => {
                this.fluidTypes.set(fluidTypes);
                this.selectionService.getProductLocations().subscribe({
                  next: (locations) => this.locations.set(locations),
                  error: (error) => this.handleError(error),
                  complete: () => this.isBusy.set(false)
                });
                this.unitSelectionCriteria.coolingCriteria = this.buildDefaultCoolingCriteria();
                this.unitSelectionCriteria.heatingCriteria = this.buildDefaultHeatingCriteria();
              },
              error: (error) => this.handleError(error)
            });
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private buildDefaultCoolingCriteria() {
    const criteria = new CoilSelectionCriteria();
    criteria.dryBulbTemperature = 75;
    criteria.wetBulbTemperature = 63;
    criteria.fluidTemperature = 45;
    criteria.temperatureChange = 10;
    criteria.fluidFlowRate = 3;
    criteria.fluidType = 'W';
    return criteria;
  }

  private buildDefaultHeatingCriteria() {
    const criteria = new CoilSelectionCriteria();
    criteria.dryBulbTemperature = 70;
    criteria.fluidTemperature = 180;
    criteria.temperatureChange = 20;
    criteria.fluidFlowRate = 3;
    criteria.fluidType = 'W';
    return criteria;
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  locationChanged(): void {
    this.previousState.set(JSON.parse(JSON.stringify(this.unitSelectionCriteria)));
    this.candidates.set(undefined!);
    this.availableModels = undefined!;
    this.unitSelectionCriteria.familyId = 0;
    this.unitSelectionCriteria.systemType = undefined!;
    this.unitSelectionCriteria.modelCode = undefined!;
    this.systemTypes.set([]);
    this.motorVoltages.set([]);
    this.motorTypes.set([]);
    this.systemTypeChanged();
    this.isBusy.set(true);
    this.handleLocationChange().subscribe({
      complete: () => {
        this.isBusy.set(false);
        this.restoreSelections();
      }, error: (error) => this.handleError(error)
    });
  }

  private restoreSelections(): void {
    if ((this.previousState()?.familyId || 0) > 0) {
      if ((this.filterTypes()?.length || 0) > 0) {
        const filtered = this.filterTypes().filter(filterType => filterType.code === this.previousState().filterType);
        if (filtered.length === 1) {
          this.unitSelectionCriteria.filterType = filtered[0].code;
        }
      }
      if ((this.motorTypes()?.length || 0) > 0) {
        const filtered = this.motorTypes().filter(entry => entry.motorCode === this.previousState().motorType);
        if (filtered.length === 1) {
          this.unitSelectionCriteria.motorType = filtered[0].motorCode;
        }
      }
      if ((this.motorVoltages()?.length || 0) > 0) {
        const filtered = this.motorVoltages().filter(entry => entry.description === this.previousState().motorVph);
        if (filtered.length === 1) {
          this.unitSelectionCriteria.motorVph = filtered[0].description;
        }
      }
      if ((this.systemTypes()?.length || 0) > 0) {
        const filtered = this.systemTypes().filter(entry => entry.code === this.previousState().systemType);
        if (filtered.length === 1) {
          this.unitSelectionCriteria.systemType = filtered[0].code;
          this.systemTypeChanged();
        }
      }
    }
  }

  private handleLocationChange(): Observable<void> {
    const subject = new Subject<void>();
    this.selectionService.getProductLocationFamilies(this.unitSelectionCriteria.locationId).subscribe({
      next: (families) => {
        this.families.set(families);
        if (this.families().length === 1) {
          this.unitSelectionCriteria.familyId = this.families()[0].id;
          this.handleFamilyChange().subscribe({
            complete: () => subject.complete(),
            error: (error) => subject.error(error),
            next: () => subject.next()
          });
        } else {
          this.loadMotorConfigurations().subscribe({
            complete: () => {
              this.loadFilterTypes().subscribe({
                complete: () => subject.complete(),
                error: (error) => subject.error(error),
                next: () => subject.next()
              });
            }, error: (error) => subject.error(error)
          });
        }
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private loadFilterTypes(): Observable<void> {
    const subject = new Subject<void>();
    this.unitSelectionCriteria.filterType = undefined!;
    this.filterTypes.set([]);
    if (this.unitSelectionCriteria.locationId > 0 && this.unitSelectionCriteria.familyId > 0) {
      this.selectionService.getLocationFamilyFilterTypes(this.unitSelectionCriteria.locationId, this.unitSelectionCriteria.familyId)
        .subscribe({
          next: (filterTypes) => {
            this.filterTypes.set(filterTypes.sort((first, second) => first.description.localeCompare(second.description)));
            if (this.filterTypes().length === 1) {
              this.unitSelectionCriteria.filterType = this.filterTypes()[0].code;
            }
            subject.next();
          }, error: (error) => subject.error(error),
          complete: () => subject.complete()
        });
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  private loadMotorTypes(): Observable<void> {
    const subject = new Subject<void>();
    this.motorTypes.set([]);
    if (this.unitSelectionCriteria.locationId > 0 && this.unitSelectionCriteria.familyId > 0) {
      const locationId = this.unitSelectionCriteria.locationId;
      const familyId = this.unitSelectionCriteria.familyId;
      const voltage = this.unitSelectionCriteria?.motorVph?.replace(/\//g, '') || '';
      if (locationId > 0 && familyId > 0 && (voltage.trim().length || 0) > 0) {
        this.selectionService.getMotorTypes(locationId, familyId, voltage).subscribe({
          next: (types) => {
            this.motorTypes.set(types);
            subject.next();
          },
          error: (error) => subject.error(error),
          complete: () => subject.complete()
        });
      } else {
        subject.complete();
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  private loadMotorConfigurations(): Observable<void> {
    const subject = new Subject<void>();
    this.unitSelectionCriteria.motorVph = undefined!;
    this.motorVoltages.set([]);
    const locationId = this.unitSelectionCriteria.locationId;
    const familyId = this.unitSelectionCriteria.familyId;
    this.selectionService.getMotorVoltages(locationId, familyId).subscribe({
      next: (voltages) => {
        this.motorVoltages.set(voltages);
        if (this.motorVoltages().length === 1) {
          this.unitSelectionCriteria.motorVph = this.motorVoltages()[0].description;
          this.motorConfigurationChanged();
        }
        subject.next();
      },
      error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  motorConfigurationChanged(): void {
    this.candidates.set(undefined!);
    this.isBusy.set(true);
    this.motorTypes.set([]);
    this.previousState.set(JSON.parse(JSON.stringify(this.unitSelectionCriteria)));
    this.loadMotorTypes().subscribe({
      complete: () => {
        this.isBusy.set(false);
        this.restoreSelections();
      },
      error: (error) => this.handleError(error)
    });
  }

  selectedModelChanged(selectedModel: RatingsModel): void {
    this.unitSelectionCriteria.modelCode = (selectedModel?.modelCode || '');
  }

  familyChanged(): void {
    this.previousState.set(JSON.parse(JSON.stringify(this.unitSelectionCriteria)));
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.candidates.set(undefined!);
    this.unitSelectionCriteria.systemType = undefined!;
    this.unitSelectionCriteria.modelCode = undefined!;
    this.unitSelectionCriteria.airflow = undefined!;
    if (!StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.coolingCriteria?.targetValue)) {
      this.unitSelectionCriteria.coolingCriteria.targetValue = undefined!;
    }
    if (!StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.heatingCriteria?.targetValue)) {
      this.unitSelectionCriteria.heatingCriteria.targetValue = undefined!;
    }
    this.systemTypes.set([]);
    this.motorVoltages.set([]);
    this.motorTypes.set([]);
    this.systemTypeChanged();
    this.availableModels = this.allModels().filter(model => model.locationId === this.unitSelectionCriteria.locationId
      && model.familyId === this.unitSelectionCriteria.familyId);
    this.handleFamilyChange().subscribe({
      complete: () => {
        this.isBusy.set(false);
        this.restoreSelections();
      }, error: (error) => this.handleError(error)
    });
  }

  private handleFamilyChange(): Observable<void> {
    const subject = new Subject<void>();
    this.loadMotorConfigurations().subscribe({
      complete: () => {
        this.loadFilterTypes().subscribe({
          complete: () => {
            this.selectionService.getProductFamilySystemTypes(this.unitSelectionCriteria.familyId).subscribe({
              next: (systemTypes) => {
                this.systemTypes.set(systemTypes);
                if (this.systemTypes().length === 1) {
                  this.unitSelectionCriteria.systemType = this.systemTypes()[0].code;
                  this.systemTypeChanged();
                }
                subject.next();
              },
              error: (error) => subject.error(error),
              complete: () => subject.complete()
            });
          }, error: (error) => subject.error(error)
        });

      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  systemTypeChanged(): void {
    this.candidates.set(undefined!);
    this.showCoolingInputs.set(false);
    this.showHydronicHeating.set(false);
    this.showHeatingInputs.set(false);
    if (this.unitSelectionCriteria.systemType?.length || 0 > 0) {
      const selectedSystem = this.systemTypes().filter(st => st.code === this.unitSelectionCriteria.systemType)[0];
      this.isRefrigerant.set(selectedSystem.refrigerant);
      this.isSteam.set(selectedSystem.steam);
      this.showCoolingInputs.set(selectedSystem.primaryCoilApplication === 'C');
      this.showHydronicHeating.set(!this.isSteam() && (selectedSystem.primaryCoilApplication === 'H' || selectedSystem.secondaryCoilApplication === 'H'));
      const showElectricHeating = selectedSystem.primaryCoilApplication === 'E' || selectedSystem.secondaryCoilApplication === 'E';
      this.showHeatingInputs.set(this.isSteam() || this.showHydronicHeating() || showElectricHeating);
      if (this.isSteam()) {
        this.unitSelectionCriteria.heatingCriteria.fluidTemperature = this.unitSelectionCriteria.metricInput ? +UnitConversionUtils.convertSteamPressure(2, this.unitSelectionCriteria.metricInput) : 2;
        this.unitSelectionCriteria.heatingCriteria.temperatureChange = undefined!;
        this.unitSelectionCriteria.heatingCriteria.fluidFlowRate = undefined!;
        this.unitSelectionCriteria.heatingCriteria.fluidType = 'W';
      } else {
        this.unitSelectionCriteria.heatingCriteria.fluidTemperature = this.unitSelectionCriteria.metricInput ? +UnitConversionUtils.convertTemperatureValue(180, this.unitSelectionCriteria.metricInput) : 180;
      }
    }
  }

  getTemperatureUnits(): string {
    return UnitConversionUtils.getTemperatureUnits(this.unitSelectionCriteria.metricInput);
  }

  getAirflowUnits(): string {
    return UnitConversionUtils.getAirflowUnits(this.unitSelectionCriteria.metricInput);
  }

  getLengthUnits(): string {
    return UnitConversionUtils.getLengthUnits(this.unitSelectionCriteria.metricInput);
  }

  getDimensionalUnits(): string {
    return UnitConversionUtils.getDimensionalUnits(this.unitSelectionCriteria.metricInput);
  }

  getEspUnits(): string {
    return UnitConversionUtils.getEspUnits(this.unitSelectionCriteria.metricInput);
  }

  metricInputChanged(): void {
    this.candidates.set(undefined!);
    const isMetric = this.unitSelectionCriteria.metricInput;
    this.unitSelectionCriteria.altitude = this.convertAltitude(isMetric);
    this.unitSelectionCriteria.airflow = this.convertAirflow(isMetric);
    this.unitSelectionCriteria.externalStaticPressure = this.convertEsp(isMetric);
    this.unitSelectionCriteria.maxWidth = this.convertDimensionalValue(this.unitSelectionCriteria.maxWidth, isMetric);
    this.unitSelectionCriteria.maxHeight = this.convertDimensionalValue(this.unitSelectionCriteria.maxHeight, isMetric);
    this.unitSelectionCriteria.maxLength = this.convertDimensionalValue(this.unitSelectionCriteria.maxLength, isMetric);
  }

  private convertDimensionalValue(value: number, isMetric: boolean): number {
    return StringUtils.isEmptyNumericValue(value) ? undefined! : this.round(UnitConversionUtils.convertDimensionalValue(value, isMetric), 3);
  }

  private round(value: string, digits: number): number {
    const numericValue = +value;
    const roundedValue = numericValue.toFixed(digits);
    return +roundedValue;
  }

  private convertEsp(isMetric: boolean): number {
    return StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.externalStaticPressure)
      ? undefined! : this.round(UnitConversionUtils.convertStaticPressure(this.unitSelectionCriteria.externalStaticPressure, isMetric), 3);
  }

  private convertAirflow(isMetric: boolean): number {
    return StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.airflow)
      ? undefined! : this.round(UnitConversionUtils.convertAirflow(this.unitSelectionCriteria.airflow, isMetric), 3);
  }

  private convertAltitude(isMetric: boolean): number {
    return StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.altitude)
      ? undefined! : this.round(UnitConversionUtils.convertAltitudeValue(this.unitSelectionCriteria.altitude, isMetric), 3);
  }

  getSelectionCandidates(): void {
    this.resultMessage.set(undefined!);
    this.isBusy.set(true);
    this.candidates.set([]);
    this.errorMessage.set(undefined!);
    if (!this.showCoolingInputs()) {
      this.unitSelectionCriteria.coolingCriteria = undefined!;
    }
    if (!this.showHeatingInputs()) {
      this.unitSelectionCriteria.heatingCriteria = undefined!;
    }
    this.selectionService.getSelectionCandidates(this.unitSelectionCriteria).subscribe({
      next: (response) => {
        if ((response?.candidates?.length > 0)) {
          this.candidates.set(response.candidates.sort((first, second) => first.targetDeviation - second.targetDeviation));
          if (!this.unitSelectionCriteria.coolingCriteria) {
            this.unitSelectionCriteria.coolingCriteria = this.buildDefaultCoolingCriteria();
          }
          if (!this.unitSelectionCriteria.heatingCriteria) {
            this.unitSelectionCriteria.heatingCriteria = this.buildDefaultHeatingCriteria();
          }
        } else {
          this.resultMessage.set(response.messages.join(', '));
        }
      },
      error: (error) => this.handleError(error),
      complete: () => this.isBusy.set(false)
    });
  }

  showFanSpeedInput(): boolean {
    if (!this.availableModels || this.availableModels.length === 0) {
      return false;
    }
    return this.availableModels.filter(model => model.airHandlerModel).length === 0;
  }

  addToUnitList(candidate: UnitSelectionCandidate): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    if ((this.scheduleId()?.trim()?.length || 0) > 0) {
      this.addUnitToSchedule(candidate);
    } else {
      let ratingUnit: RatingUnit = new RatingUnit();
      ratingUnit.personId = this.personLogin.person.personId;
      ratingUnit.orgUnitId = this.personLogin.person.primaryOrgUnitId;
      ratingUnit.productId = candidate.modelSizeCharacteristic.ratingsModel.productId;
      ratingUnit.modelCode = candidate.ratingRequest.Model;
      ratingUnit.sizeCode = candidate.ratingRequest.Size;
      ratingUnit.systemTypeCode = candidate.systemType.code;
      ratingUnit.coilRowCode = candidate.coilRowCode;
      ratingUnit.powerSupplyCode = this.getSelectedMotorVoltageCode(candidate.ratingRequest.MotorConfiguration);
      ratingUnit.motorTypeCode = this.motorTypes().filter(type => type.motorCode === candidate.ratingRequest.MotorType)[0].guruModelValue;
      ratingUnit.airFilterCode = candidate.ratingRequest.Filter || '';
      ratingUnit.electricHeatCode = (candidate.ratingRequest.ElectricHeatRequest?.ElectricHeatKilowatts || 0).toString();
      ratingUnit.request = candidate.ratingRequest;
      ratingUnit.response = candidate.ratingResponse;
      this.ratingUnitService.saveUnit(ratingUnit).subscribe({
        next: () => this.isBusy.set(false),
        error: (error) => this.handleError(error)
      });
    }
  }

  private addUnitToSchedule(candidate: UnitSelectionCandidate): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.scheduleService.getSchedule(this.scheduleId()).subscribe({
      next: (schedule) => {
        const inputDialog = this.dialog.open(AddScheduleLineDialogComponent, {
          height: '200px',
          width: '600px',
          data: {scheduleId: schedule.id.entityId, candidate: candidate},
        });
        inputDialog.afterClosed().subscribe({
          next: (result: ScheduleLineInput) => {
            if (result) {
              this.scheduleService.addLineToSchedule(result).subscribe({
                next: () => {
                  this.router.navigate(['schedules', schedule.id.entityId]);
                }, error: (error) => this.handleError(error)
              });
            } else {
              this.isBusy.set(false);
            }
          }, error: (error) => this.handleError(error),
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private getSelectedMotorVoltageCode(description: string): string {
    if ((this.motorVoltages()?.length || 0) > 0) {
      const selectedVoltage = this.motorVoltages().filter(voltage => voltage.description === description);
      if (selectedVoltage.length === 1) {
        return selectedVoltage[0].code;
      }
    }
    return '';
  }

  getInvalidReasons(): string {
    const reasons: string[] = [];
    if (this.unitSelectionCriteria.locationId === 0) {
      reasons.push('Please select a Location');
    }
    if (this.unitSelectionCriteria.familyId === 0) {
      reasons.push('Please select a Family');
    }
    if (this.isBlankString(this.unitSelectionCriteria.systemType)) {
      reasons.push('Please select a System Type');
    } else {
      if (this.isBlankString(this.unitSelectionCriteria.motorVph)) {
        reasons.push('Please select a Unit Power Supply');
      } else {
        if (this.isBlankString(this.unitSelectionCriteria.motorType)) {
          reasons.push('Please select a Motor Type');
        }
      }
      if (this.filterTypes() && this.filterTypes().length > 0) {
        if (this.isBlankString(this.unitSelectionCriteria.filterType)) {
          reasons.push('Please select a Filter');
        }
      }
      if (!this.showFanSpeedInput()) {
        if (StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.airflow) || this.unitSelectionCriteria.airflow < 0) {
          reasons.push('Please enter an Airflow');
        }
      }
      if (StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.altitude)) {
        reasons.push('Please enter a value for Altitude');
      } else {
        const altitudeLimits = this.conditionLimits().filter(limit => limit.description === 'Altitude');
        if (altitudeLimits.length > 0) {
          const limit = altitudeLimits[0];
          if (this.unitSelectionCriteria.altitude < limit.minValue || this.unitSelectionCriteria.altitude > limit.maxValue) {
            reasons.push('Altitude must be between ' + limit.minValue + ' and ' + limit.maxValue);
          }
        }
      }
      if (StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.externalStaticPressure) || this.unitSelectionCriteria.externalStaticPressure < 0) {
        reasons.push('Please enter an ESP of 0 or more');
      }
      if (this.showCoolingInputs()) {
        if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.coolingCriteria.fluidTemperature)) {
          if (this.isRefrigerant()) {
            reasons.push('Please enter a value for the Saturation Suction Temperature');
          } else {
            reasons.push('Please enter a value for the Cooling Entering Fluid Temperature');
          }
        } else {
          if (!StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.coolingCriteria.dryBulbTemperature)) {
            if ('E' === this.unitSelectionCriteria.coolingCriteria.fluidType || 'P' === this.unitSelectionCriteria.coolingCriteria.fluidType) {
              if (this.unitSelectionCriteria.coolingCriteria.dryBulbTemperature <= this.unitSelectionCriteria.coolingCriteria.fluidTemperature) {
                reasons.push('The Cooling Entering Dry Bulb Temperature must be higher than the Cooling Entering Fluid Temperature');
              }
            } else if ('W' === this.unitSelectionCriteria.coolingCriteria.fluidType) {
              if (this.unitSelectionCriteria.coolingCriteria.dryBulbTemperature - 15 < this.unitSelectionCriteria.coolingCriteria.fluidTemperature) {
                reasons.push('The Cooling Entering Dry Bulb Temperature must be at least 15 degrees higher than the Cooling Entering Fluid Temperature');
              }
            }
          }
        }
        if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.coolingCriteria.dryBulbTemperature)) {
          reasons.push('Please enter a value for the Cooling Entering Dry Bulb Temperature');
        } else {
          const limits = this.conditionLimits().filter(limit => limit.coolingApplication && limit.fluidTypeCode === this.unitSelectionCriteria.coolingCriteria.fluidType && limit.description.endsWith('Dry Bulb Temperature'));
          if (limits.length > 0) {
            const limit = limits[0];
            if (this.unitSelectionCriteria.coolingCriteria.dryBulbTemperature < limit.minValue || this.unitSelectionCriteria.coolingCriteria.dryBulbTemperature > limit.maxValue) {
              reasons.push('Cooling Entering Dry Bulb Temperature must be between ' + limit.minValue + ' and ' + limit.maxValue);
            }
          }
        }
        if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.coolingCriteria.wetBulbTemperature)) {
          reasons.push('Please enter a value for the Cooling Entering Wet Bulb Temperature');
        } else {
          const limits = this.conditionLimits().filter(limit => limit.coolingApplication && limit.fluidTypeCode === this.unitSelectionCriteria.coolingCriteria.fluidType && limit.description.endsWith('Wet Bulb Temperature'));
          if (limits.length > 0) {
            const limit = limits[0];
            if (this.unitSelectionCriteria.coolingCriteria.wetBulbTemperature < limit.minValue || this.unitSelectionCriteria.coolingCriteria.wetBulbTemperature > limit.maxValue) {
              reasons.push('Cooling Entering Wet Bulb Temperature must be between ' + limit.minValue + ' and ' + limit.maxValue);
            }
          }
        }
        if (this.unitSelectionCriteria.coolingCriteria.fluidType === 'E' || this.unitSelectionCriteria.coolingCriteria.fluidType === 'P') {
          if (StringUtils.isZeroOrEmpty(+this.unitSelectionCriteria.coolingCriteria.glycolPercent)) {
            reasons.push('Please enter a value for the Cooling Glycol Percentage');
          } else {
            const limits = this.conditionLimits().filter(limit => limit.coolingApplication && limit.fluidTypeCode === this.unitSelectionCriteria.coolingCriteria.fluidType && limit.description === 'Glycol Percentage');
            if (limits.length > 0) {
              const limit = limits[0];
              if (+this.unitSelectionCriteria.coolingCriteria.glycolPercent < limit.minValue || +this.unitSelectionCriteria.coolingCriteria.glycolPercent > limit.maxValue) {
                reasons.push('Cooling Glycol Percentage must be between ' + limit.minValue + ' and ' + limit.maxValue);
              }
            }
          }
        }
        if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.coolingCriteria.targetValue)) {
          reasons.push('Please enter a target value for Cooling');
        } else {
          if (this.unitSelectionCriteria.coolingCriteria.targetType === 'AIRFLOW') {
            if (this.showHeatingInputs() && this.unitSelectionCriteria.heatingCriteria.targetType === 'AIRFLOW' && !StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.heatingCriteria.targetValue)) {
              if (this.unitSelectionCriteria.coolingCriteria.targetValue !== this.unitSelectionCriteria.heatingCriteria.targetValue) {
                reasons.push('Please specify the same target Airflow value for Cooling and Heating');
              }
            }
          }
        }
      }
      if (this.showHeatingInputs()) {
        if (this.showHydronicHeating()) {
          if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.heatingCriteria.fluidTemperature)) {
            reasons.push('Please enter a value for the Heating Entering Fluid Temperature');
          } else {
            if (!StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.heatingCriteria.dryBulbTemperature)) {
              if (this.unitSelectionCriteria.heatingCriteria.fluidTemperature <= this.unitSelectionCriteria.heatingCriteria.dryBulbTemperature) {
                reasons.push('The Heating Entering Fluid Temperature must be higher than the Entering Dry Bulb Temperature');
              }
            }
          }
          if (this.unitSelectionCriteria.heatingCriteria.fluidType === 'E' || this.unitSelectionCriteria.heatingCriteria.fluidType === 'P') {
            if (StringUtils.isZeroOrEmpty(+this.unitSelectionCriteria.heatingCriteria.glycolPercent)) {
              reasons.push('Please enter a value for the Heating Glycol Percentage');
            } else {
              const limits = this.conditionLimits().filter(limit => !limit.coolingApplication && limit.fluidTypeCode === this.unitSelectionCriteria.heatingCriteria.fluidType && limit.description === 'Glycol Percentage');
              if (limits.length > 0) {
                const limit = limits[0];
                if (+this.unitSelectionCriteria.heatingCriteria.glycolPercent < limit.minValue || +this.unitSelectionCriteria.heatingCriteria.glycolPercent > limit.maxValue) {
                  reasons.push('Heating Glycol Percentage must be between ' + limit.minValue + ' and ' + limit.maxValue);
                }
              }
            }
          }
        }
        if (this.isSteam()) {
          if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.heatingCriteria.fluidTemperature)) {
            reasons.push('Please enter a value for the Entering Steam Pressure');
          } else {
            const limits = this.conditionLimits().filter(limit => limit.description === 'Steam Heating Entering Steam Pressure');
            if (limits.length > 0) {
              const limit = limits[0];
              if (this.unitSelectionCriteria.heatingCriteria.fluidTemperature < limit.minValue || this.unitSelectionCriteria.heatingCriteria.fluidTemperature > limit.maxValue) {
                reasons.push('Steam Pressure must be between ' + limit.minValue + ' and ' + limit.maxValue);
              }
            }
          }
        }
        if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.heatingCriteria.dryBulbTemperature)) {
          reasons.push('Please enter a value for the Heating Entering Dry Bulb Temperature');
        } else {
          const limits = this.conditionLimits().filter(limit => !limit.coolingApplication && limit.fluidTypeCode === this.unitSelectionCriteria.heatingCriteria.fluidType && limit.description.endsWith('Dry Bulb Temperature'));
          if (limits.length > 0) {
            const limit = limits[0];
            if (this.unitSelectionCriteria.heatingCriteria.dryBulbTemperature < limit.minValue || this.unitSelectionCriteria.heatingCriteria.dryBulbTemperature > limit.maxValue) {
              reasons.push('Heating Entering Dry Bulb Temperature must be between ' + limit.minValue + ' and ' + limit.maxValue);
            }
          }
        }
        if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.heatingCriteria.targetValue)) {
          reasons.push('Please enter a target value for Heating');
        } else {
          if (this.unitSelectionCriteria.heatingCriteria.targetType === 'AIRFLOW') {
            if (this.showCoolingInputs() && this.unitSelectionCriteria.coolingCriteria.targetType === 'AIRFLOW' && !StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.coolingCriteria.targetValue)) {
              if (this.unitSelectionCriteria.heatingCriteria.targetValue !== this.unitSelectionCriteria.coolingCriteria.targetValue) {
                reasons.push('Please specify the same target Airflow value for Cooling and Heating');
              }
            }
          }
        }
      }
    }
    return reasons.join('\n');
  }

  private isBlankString(input: string): boolean {
    return (input?.trim()?.length || 0) === 0;
  }

  isInvalid(): boolean {
    return (this.getInvalidReasons()?.trim()?.length || 0) > 0;
  }

  getPressureUnits(): string {
    return UnitConversionUtils.getPressureUnits(this.unitSelectionCriteria.metricInput);
  }

  getProjectNotice(): string {
    if ((this.scheduleId()?.trim()?.length || 0) > 0) {
      return 'Units will be added to the selected schedule';
    }
    return undefined!;
  }

  isFieldInvalid(fieldName: string): boolean {
    switch (fieldName) {
      case 'Location':
        return (this.unitSelectionCriteria?.locationId || 0) === 0;
      case 'Family':
        return (this.unitSelectionCriteria?.familyId || 0) === 0;
      case 'SystemType':
        return (this.unitSelectionCriteria?.systemType || '') === '';
      case 'PowerSupply':
        return this.isBlankString(this.unitSelectionCriteria.motorVph);
      case 'MotorType':
        return this.isBlankString(this.unitSelectionCriteria.motorType);
      case 'Filter':
        if (this.filterTypes() && this.filterTypes().length > 0) {
          return this.isBlankString(this.unitSelectionCriteria.filterType);
        } else {
          return false;
        }
      case 'Airflow':
        if (!this.showFanSpeedInput()) {
          return StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.airflow) || this.unitSelectionCriteria.airflow < 0;
        } else {
          return false;
        }
      case 'Altitude':
        return StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.altitude) || this.unitSelectionCriteria.altitude < 0;
      case 'ESP':
        return StringUtils.isEmptyNumericValue(this.unitSelectionCriteria.externalStaticPressure) || this.unitSelectionCriteria.externalStaticPressure < 0;
      case 'HeatingTargetValue':
        if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.heatingCriteria.targetValue)) {
          return true;
        }
        if (this.unitSelectionCriteria.heatingCriteria.targetType === 'AIRFLOW') {
          if (this.showCoolingInputs()) {
            if (this.unitSelectionCriteria.coolingCriteria.targetType === 'AIRFLOW') {
              return this.unitSelectionCriteria.heatingCriteria.targetValue !== this.unitSelectionCriteria.coolingCriteria.targetValue;
            }
          }
        }
        return false;
      case 'CoolingTargetValue':
        if (StringUtils.isZeroOrEmpty(this.unitSelectionCriteria.coolingCriteria.targetValue)) {
          return true;
        }
        if (this.unitSelectionCriteria.coolingCriteria.targetType === 'AIRFLOW') {
          if (this.showCoolingInputs()) {
            if (this.unitSelectionCriteria.heatingCriteria.targetType === 'AIRFLOW') {
              return this.unitSelectionCriteria.coolingCriteria.targetValue !== this.unitSelectionCriteria.heatingCriteria.targetValue;
            }
          }
        }
        return false;
      default:
        return false;
    }
  }

}
